import { memo } from "react";
import { cn } from "@/lib/utils";
import { formatMonth, monthOf, trend3vs3 } from "@/lib/keyword-volume-parser";

interface KeywordVolumeCellProps {
  /** [M1, M2, ..., M12]; null = no data for that month. */
  months: (number | null)[];
  /** Calendar month of M1, "YYYY-MM-01". */
  anchor: string;
  /** Precomputed trend if the source has one; else computed from months. */
  trend?: number | null;
}

const W = 56;
const H = 18;

/**
 * Sparkline of 12 months (oldest left, M1 right), M1's value and the 3-vs-3
 * trend. Hover lists every month as "серп. 2026 · 30".
 * ponytail: native title as the tooltip - no tooltip primitive in the project.
 */
export const KeywordVolumeCell = memo(({ months, anchor, trend }: KeywordVolumeCellProps) => {
  if (months.every((v) => v == null)) {
    return <span className="text-muted-foreground">—</span>;
  }

  const chrono = [...months].reverse(); // M12 .. M1
  const max = Math.max(1, ...chrono.map((v) => v ?? 0));
  const step = W / (chrono.length - 1);
  const points = chrono
    .map((v, i) => (v == null ? null : `${(i * step).toFixed(1)},${(H - 1 - ((v / max) * (H - 2))).toFixed(1)}`))
    .filter(Boolean)
    .join(" ");

  const t = trend === undefined ? trend3vs3(months) : trend;
  const title = months
    // Month names only: the M1..M12 columns count from the newest export, this
    // from the row's own latest month, so "M1" would mean two different months.
    .map((v, i) => `${formatMonth(monthOf(anchor, i + 1), "en-US")} · ${v == null ? "—" : v.toLocaleString("en-US")}`)
    .join("\n");

  return (
    <span className="inline-flex items-center gap-2 tabular-nums" title={title}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden className="text-primary">
        <polyline points={points} fill="none" stroke="currentColor" strokeWidth={1.5} />
      </svg>
      <span className="font-medium">{months[0] == null ? "—" : months[0].toLocaleString("en-US")}</span>
      {t != null && (
        <span
          className={cn(
            "text-xs",
            t > 0 && "signal-text-positive",
            t < 0 && "signal-text-negative",
            t === 0 && "text-muted-foreground",
          )}
        >
          {t > 0 ? "↑" : t < 0 ? "↓" : ""}
          {Math.abs(t)}%
        </span>
      )}
    </span>
  );
});

KeywordVolumeCell.displayName = "KeywordVolumeCell";
